/**
 * multiAgentSelectors — pure selector functions for multiAgentStore.
 *
 * Rules:
 *  - Selectors are pure functions: (state) => derivedValue
 *  - No side effects, no API calls, no React hooks
 *  - Colocate derived state here so components never inline derivations
 *  - Matching hooks (useXxx) live in hooks/multi-agent/useMultiAgentSelectors.ts
 */
import type { MultiAgentState } from "./multiAgentStore";

const TERMINAL_STATUSES = new Set(["done", "failed", "cancelled"]);

// ── Intent / chat ──────────────────────────────────────────────────────────

/** The first user message in the current chat — the "question" for this run. */
export const selectUserIntent = (s: MultiAgentState): string =>
  s.chat.find((m) => m.role === "user")?.text ?? "";

/** Attachments carried on the sent user message — inline bubble thumbnails (#11).
 *  Returns a STABLE shared empty array when there are none — a fresh `[]` literal
 *  here would be a new reference every render and send Zustand into an infinite
 *  re-render loop ("Maximum update depth exceeded"). */
const EMPTY_ATTACHMENTS: NonNullable<MultiAgentState["chat"][number]["attachments"]> = [];
export const selectUserAttachments = (s: MultiAgentState) =>
  s.chat.find((m) => m.role === "user")?.attachments ?? EMPTY_ATTACHMENTS;

// ── Session lifecycle ──────────────────────────────────────────────────────

/** True when a run is in progress or results exist (not idle + empty). */
export const selectHasActiveSession = (s: MultiAgentState): boolean =>
  s.status !== "idle" || s.runId != null || s.thread.length > 0;

/** True when the current run has reached a terminal state. */
export const selectIsTerminal = (s: MultiAgentState): boolean =>
  TERMINAL_STATUSES.has(s.status);

/** True when the chat column should show the idle hero (no content at all). */
export const selectIsFreshEmptySession = (s: MultiAgentState): boolean =>
  s.status === "idle" && !s.runId && !s.sessionId && s.thread.length === 0;

// ── Agents / nodes ─────────────────────────────────────────────────────────

/** Agent nodes as a flat array (Object.values memoized via selector). */
export const selectAgentNodes = (s: MultiAgentState) =>
  Object.values(s.nodes);

/** Nodes currently running or waiting for a tool. */
export const selectBusyNodes = (s: MultiAgentState) =>
  Object.values(s.nodes).filter(
    (n) => n.status === "running" || n.status === "tool_calling",
  );

// ── Artifacts ─────────────────────────────────────────────────────────────

/** Artifacts produced by a specific run. Falls back to the artifactIds
 *  snapshot for older thread entries that pre-date runId tagging. */
export const selectArtifactsByRun =
  (runId: string, artifactIds: string[]) =>
  (s: MultiAgentState) =>
    s.artifacts.filter((a) =>
      a.runId ? a.runId === runId : artifactIds.includes(a.id),
    );

/** The N most recent artifacts (for the right-rail tile). */
export const selectRecentArtifacts =
  (limit = 12) =>
  (s: MultiAgentState) =>
    s.artifacts.slice(-limit).reverse();

// ── Cost / stats ───────────────────────────────────────────────────────────

/** Total token count for the cost display. */
export const selectTotalTokens = (s: MultiAgentState): number =>
  s.cost ? s.cost.tokensIn + s.cost.tokensOut : 0;

// ── Follow-ups ─────────────────────────────────────────────────────────────

/** Follow-up suggestions: backend-provided first, then static prompts fallback. */
export const selectEffectiveFollowUps = (
  backendFollowUps: MultiAgentState["followUps"],
  staticPrompts: Array<{ label: string; hint: string; question: string }>,
) => (backendFollowUps.length > 0 ? backendFollowUps : staticPrompts);

// ── Streaming ──────────────────────────────────────────────────────────────

/** Merged streaming text across all nodes (for pre-synthesis display). */
export const selectStreamingText = (s: MultiAgentState): string =>
  Object.values(s.textBuffer).join("");

/** True when any node is actively streaming reasoning or text. */
export const selectIsStreaming = (s: MultiAgentState): boolean =>
  Object.keys(s.textBuffer).length > 0 || Object.keys(s.reasoningBuffer).length > 0;
